import {fromPromise} from "@apollo/client";
import {onError} from "@apollo/client/link/error";
// @ts-ignore
import {createUploadLink} from "apollo-upload-client";
import {includes, some} from "lodash";

import {fetchAuthorized} from "@atg-shared/auth";
import {bffAuthentication} from "@atg-shared/auth/domain/authActions";
import {addAuthHeaders, fetchFileUpload, pureFetch} from "@atg-shared/fetch";
import {noTrans} from "@atg-shared/no-trans";
import {MarketUtils} from "@atg-tillsammans-shared/utils";

import type {ApolloLink} from "@apollo/client";
import type {AtgResponse} from "@atg-shared/fetch-types";
import type {GraphQLError} from "graphql";
import type {Logger} from "@atg-shared/log";
import type {Dispatch} from "redux";

interface AccessTokenResult {
    token: string;
    hadToLogin: boolean;
}

export const errorLink = (dispatch: Dispatch, logger: Logger): ApolloLink =>
    onError(({graphQLErrors, networkError, operation, forward}) => {
        if (graphQLErrors) {
            const hasAuthError = some(graphQLErrors, (error: GraphQLError) =>
                includes(error?.extensions?.errorCode as string, "ACCESS_DENIED"),
            );

            if (hasAuthError) {
                const promise = new Promise<AccessTokenResult>((resolve, reject) => {
                    dispatch(bffAuthentication(resolve, reject));
                });

                return fromPromise(promise).flatMap(
                    ({hadToLogin, token}: AccessTokenResult) => {
                        if (!hadToLogin) {
                            operation.setContext({
                                headers: {
                                    ...operation.getContext().headers,
                                    authorization: `Bearer ${token}`,
                                },
                            });
                        }

                        return forward(operation);
                    },
                );
            }
        }

        if (graphQLErrors || networkError) {
            logger.warn("apolloClientError", {graphQLErrors, networkError});
        }

        return undefined;
    });

const isNewAtgApi = (uri: string) => uri.includes("https://api.");

const getAllowedContextHeaders = (headers?: HeadersInit) => {
    const allowedHeaders = [noTrans("x-sales-channel")];

    if (!headers) return {};

    return Object.fromEntries(
        Object.entries(headers).filter(([key]) => allowedHeaders.includes(key)),
    );
};

export const uploadLink = () =>
    createUploadLink({
        uri: "/services/tokenized-proxy/team/api/graphql",
        fetch: async (uri: string, options: RequestInit): Promise<Response> => {
            // Remove unnecessary request headers which are set in atgFetch().
            const {headers, ...optionsWithoutHeaders} = options;
            const contextHeaders = getAllowedContextHeaders(headers);

            function mapResponse(response: AtgResponse<unknown>): Response {
                if (!response) {
                    return new Response(null, {
                        status: 500,
                        statusText: noTrans(`No response: ${uri}`),
                    });
                }

                return new Response(JSON.stringify(response.data), {
                    status: response.meta.code,
                    statusText: response.meta.statusText,
                });
            }

            if (options.body instanceof FormData) {
                return fetchAuthorized(
                    uri,
                    {
                        ...optionsWithoutHeaders,
                        method: "POST",
                        headers: addAuthHeaders(contextHeaders),
                    },
                    {
                        memberFlowEnabled: false,
                    },
                    fetchFileUpload,
                ).then(mapResponse);
            }

            // For public api endpoint (e.g. https://api.atg.se/team)
            if (isNewAtgApi(uri)) {
                return pureFetch(uri, {
                    body: optionsWithoutHeaders.body,
                    method: optionsWithoutHeaders.method,
                    headers: {
                        "Content-Type": "application/json",
                        ...MarketUtils.getMarketHeaders(),
                    },
                }).then(mapResponse);
            }

            return fetchAuthorized(
                uri,
                {...optionsWithoutHeaders, headers: addAuthHeaders(contextHeaders)},
                {
                    memberFlowEnabled: false,
                },
            ).then(mapResponse);
        },
    });
